import { useEffect } from 'react';
import {
  Editor,
  EditorContent,
  JSONContent,
  Node,
  useEditor,
} from '@tiptap/react';
import { CharacterCount, UndoRedo } from '@tiptap/extensions';
import {
  Fragment,
  Schema,
  Slice,
} from '@tiptap/pm/model';

import { classNames } from '../../../../common/lib/classNames';

import { mergeTagAtomExtension, mergeTagHighlightExtension } from './TextEditor';

// A doc that holds exactly one paragraph of plain text, so Enter does nothing
const SubjectDocument = Node.create({ name: `doc`, topNode: true, content: `paragraph` });
const SubjectParagraph = Node.create({
  name: `paragraph`,
  content: `text*`,
  parseHTML: () => [{ tag: `p` }],
  renderHTML: () => [`p`, 0],
});
const SubjectText = Node.create({ name: `text` });

export const subjectExtensions = (limit: number) => [
  SubjectDocument,
  SubjectParagraph,
  SubjectText,
  UndoRedo,
  CharacterCount.configure({ limit }),
  mergeTagHighlightExtension,
  mergeTagAtomExtension,
];

export const subjectDoc = (text: string): JSONContent => ({
  type: `doc`,
  content: [{ type: `paragraph`, content: text ? [{ type: `text`, text }] : [] }],
});

export const singleLine = (text: string) => text.replace(/\s*[\r\n]+\s*/g, ` `);

// Pasted blocks (from html or plain text) would otherwise be cut to the first one.
// Flattened here rather than in handlePaste so the paste still counts as one for the character limit
export const flattenPaste = (slice: Slice, schema: Schema) => {
  const text = singleLine(slice.content.textBetween(0, slice.content.size, ` `));

  return text ? new Slice(Fragment.from(schema.text(text)), 0, 0) : Slice.empty;
};

export interface ISubjectLineProps {
  value: string;
  onChange: (subject: string) => void;
  maxLength: number;
  invalid?: boolean;
}

// Single line editor shown above the body toolbar, so merge tags show as pills here too
export function SubjectLine({ value, onChange, maxLength, invalid, onCreate, onFocus }: ISubjectLineProps & {
  onCreate: (editor: Editor) => void;
  onFocus: () => void;
}) {
  const editor = useEditor({
    extensions: subjectExtensions(maxLength),
    content: subjectDoc(value),
    onCreate: ({ editor: e }) => onCreate(e),
    onFocus,
    onUpdate: ({ editor: e }) => onChange(e.getText()),
    // Keeps the character count current
    shouldRerenderOnTransaction: true,
    editorProps: {
      transformPasted: (slice, view) => flattenPaste(slice, view.state.schema),
      attributes: {
        'aria-label': `Subject`,
        class: `subject-editor py-2.5 text-sm text-gray-900 focus:outline-none`,
      },
    },
  });

  // Picks up changes from outside, like an AI rewrite
  useEffect(() => {
    if (editor && value !== editor.getText()) {
      editor.commands.setContent(subjectDoc(value), { emitUpdate: false });
    }
  }, [editor, value]);

  const length = editor?.storage.characterCount.characters() ?? value.length;

  return (
    <div className={ classNames(
      `flex items-center gap-3 rounded-t-md border border-b-0 bg-white px-3`,
      invalid ? `border-red-600` : `border-gray-300`,
    ) }>
      <span className={ `text-sm font-medium text-gray-500` }>{ `Subject` }</span>
      <EditorContent
        editor={ editor }
        className={ `min-w-0 flex-1` } />
      <span className={ classNames(`text-xs tabular-nums`, length >= maxLength ? `text-red-600` : `text-gray-400`) }>
        { `${length}/${maxLength}` }
      </span>
    </div>
  );
}
